import type { Route, RouteStep } from './routes_state';

/**
 * Pure route-selection + step-shipment helpers.
 * No state, no graphdb, no svelte — unit-testable in node.
 */

/**
 * Single read path for the current route.
 * Persisted `assigned_route_id` wins iff it is still present in `active_routes`;
 * otherwise the time-window heuristic decides.
 */
export function resolve_current_route(active_routes: Route[], persisted_id?: string | null): Route | null {
	if (!active_routes?.length) return null;

	if (persisted_id) {
		const persisted_route = active_routes.find((route) => route.id === persisted_id);
		if (persisted_route) return persisted_route;
	}

	if (active_routes.length === 1) return active_routes[0];

	for (const route of active_routes) {
		const last_step = route.Steps[route.Steps.length - 1];
		if (!last_step) continue;

		const now_time = Date.now();
		const start_eta = Date.parse(route.computed_start_time!);
		const end_eta = Date.parse(last_step.eta!);
		if (start_eta < now_time && end_eta > now_time) return route;

		if (now_time > start_eta && now_time - start_eta < 1000 * 60 * 60 * 12) return route;
	}

	const assigned_route = [...active_routes].sort(
		(a, b) => Date.parse(a.computed_start_time!) - Date.parse(b.computed_start_time!)
	)[0];
	return assigned_route ?? null;
}

/**
 * Replace the returned step-shipment entry inside `route.Steps[step_id].Shipments`
 * (pickup/deliver response application). Returns a new route object; null when
 * the step or the matching shipment entry is absent.
 */
export function apply_returned_step_shipment(
	route: Route,
	returned: RouteStep['Shipments'][number],
	step_id: string
): Route | null {
	if (!route || !returned || !step_id) return null;

	let matched = false;
	const next_steps = route.Steps.map((step) => {
		if (step.id !== step_id) return step;
		const next_shipments = step.Shipments.map((entry) => {
			if (entry.id !== returned.id) return entry;
			matched = true;
			return returned;
		});
		return { ...step, Shipments: next_shipments };
	});

	if (!matched) return null;
	return { ...route, Steps: next_steps };
}
